import React from 'react';
import { evaluateResistanceAlerts } from '../utils/microbiologyTemplateEngine';

export default function WorkbenchSidebar({
  data,
  activeIsolateIdx,
  onOpenAiModal,
  onOpenReportModal,
  onSelectTab,
  isCollapsed = false,
  onToggleCollapse
}) {
  if (!data) return null;

  const currentIsolate = data?.isolates?.[activeIsolateIdx] || null;
  const isolateList = data?.isolates || [];

  // Susceptibility breakdown calculation
  const currentAst = currentIsolate?.astResults || [];
  const totalTested = currentAst.filter((r) => r.measuredValue !== null && r.measuredValue !== undefined).length;
  const sCount = currentAst.filter((r) => r.finalInterpretation === 'S').length;
  const iCount = currentAst.filter((r) => r.finalInterpretation === 'I' || r.finalInterpretation === 'SDD').length;
  const rCount = currentAst.filter((r) => r.finalInterpretation === 'R').length;

  const resistanceAlerts = currentIsolate ? evaluateResistanceAlerts(currentIsolate) : [];

  return (
    <aside className={`wb-clinical-snapshot-drawer ${isCollapsed ? 'collapsed' : ''}`} aria-label="Clinical Summary Sidebar">
      {!isCollapsed && (
        <>
          {/* Header with Collapse Toggle */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: '6px' }}>
            <span style={{ fontSize: '0.78rem', fontWeight: '800', color: '#0f172a', letterSpacing: '0.02em' }}>
              CLINICAL SNAPSHOT
            </span>
            {onToggleCollapse && (
              <button
                type="button"
                onClick={onToggleCollapse}
                style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '700' }}
                title="Collapse Sidebar"
              >
                &rarr;|
              </button>
            )}
          </div>

          {/* 1. Quick Case Status & Culture Metrics */}
          <div className="wb-snapshot-card">
            <h4 className="wb-snapshot-heading">Case Metrics</h4>
            <div className="wb-snapshot-data-list">
              <div className="wb-snapshot-row">
                <span className="wb-snapshot-label">Case:</span>
                <span
                  className="wb-snapshot-value"
                  style={{
                    color: data.approved ? '#15803d' : data.cultureStatus === 'Growth' ? '#0284c7' : '#d97706'
                  }}
                >
                  ● {data.approved ? 'Released' : data.inoculationDate ? 'Incubating' : 'Processing'}
                </span>
              </div>

              <div className="wb-snapshot-row">
                <span className="wb-snapshot-label">Culture:</span>
                <strong
                  className="wb-snapshot-value"
                  style={{
                    color:
                      data.cultureStatus === 'Growth'
                        ? '#b91c1c'
                        : data.cultureStatus === 'No Growth'
                        ? '#15803d'
                        : '#0284c7'
                  }}
                >
                  {data.cultureStatus || 'Pending'}
                </strong>
              </div>

              <div className="wb-snapshot-row">
                <span className="wb-snapshot-label">Colony:</span>
                <span className="wb-snapshot-value">{data.colonyCount || 'Pending'}</span>
              </div>

              <div className="wb-snapshot-row">
                <span className="wb-snapshot-label">Isolates:</span>
                <strong className="wb-snapshot-value">{isolateList.length}</strong>
              </div>

              <div className="wb-snapshot-row">
                <span className="wb-snapshot-label">AST:</span>
                <span
                  className="wb-snapshot-value"
                  style={{
                    color: totalTested > 0 ? '#15803d' : '#d97706',
                    fontWeight: '600'
                  }}
                >
                  {totalTested > 0 ? `${totalTested} Tested` : 'Pending'}
                </span>
              </div>

              <div className="wb-snapshot-row">
                <span className="wb-snapshot-label">Report:</span>
                <span
                  className="wb-snapshot-value"
                  style={{
                    fontWeight: '700',
                    color: data.approved ? '#15803d' : '#64748b'
                  }}
                >
                  {data.approved ? '✓ Released' : 'Not Ready'}
                </span>
              </div>
            </div>
          </div>

          {/* 2. Susceptibility Breakdown */}
          <div className="wb-snapshot-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <h4 className="wb-snapshot-heading" style={{ margin: 0 }}>AST Summary</h4>
              <span style={{ fontSize: '0.7rem', color: '#64748b' }}>
                {currentIsolate ? `Isolate #${currentIsolate.isolateNumber || activeIsolateIdx + 1}` : 'No Isolate'}
              </span>
            </div>

            {currentIsolate ? (
              <div>
                <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                  <div className="wb-sir-badge s" style={{ flex: 1 }}>S: {sCount}</div>
                  <div className="wb-sir-badge i" style={{ flex: 1 }}>I: {iCount}</div>
                  <div className="wb-sir-badge r" style={{ flex: 1 }}>R: {rCount}</div>
                </div>

                <div style={{ fontSize: '0.72rem', color: '#64748b', textAlign: 'right' }}>
                  Tested: <strong>{totalTested}</strong> / {currentAst.length}
                </div>
              </div>
            ) : (
              <div style={{ fontSize: '0.74rem', color: '#94a3b8', padding: '4px 0' }}>
                No isolate recorded
              </div>
            )}
          </div>

          {/* 3. Resistance Alert Warnings */}
          {resistanceAlerts.length > 0 && (
            <div className="wb-snapshot-card" style={{ background: '#fef2f2', borderColor: '#fecaca' }}>
              <h4 className="wb-snapshot-heading" style={{ color: '#b91c1c' }}>
                ⚠️ Resistance Flags ({resistanceAlerts.length})
              </h4>
              <ul style={{ margin: 0, paddingLeft: '14px', fontSize: '0.74rem', color: '#dc2626' }}>
                {resistanceAlerts.map((a, i) => (
                  <li
                    key={i}
                    style={{ cursor: 'pointer', textDecoration: 'underline', marginBottom: '2px' }}
                    onClick={() => onSelectTab('resistance')}
                  >
                    {a.name}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* 4. Active Standard */}
          <div className="wb-snapshot-card">
            <h4 className="wb-snapshot-heading">Clinical Standard</h4>
            <div style={{ fontSize: '0.78rem', fontWeight: '800', color: '#0f172a' }}>
              EUCAST Breakpoints
            </div>
            <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
              v16.1 &bull; 2026 Guidelines
            </div>
          </div>

          {/* 5. Actions */}
          <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: '6px', paddingTop: '8px' }}>
            <button
              type="button"
              className="wb-btn-secondary-clinical"
              style={{ width: '100%', justifyContent: 'center', fontSize: '0.76rem' }}
              onClick={onOpenReportModal}
            >
              Preview Report
            </button>
          </div>
        </>
      )}

      {isCollapsed && (
        <div
          onClick={onToggleCollapse}
          title="Click to expand Clinical Snapshot"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '8px 4px',
            height: '100%',
            cursor: 'pointer',
            userSelect: 'none'
          }}
        >
          <button
            type="button"
            style={{
              background: 'none',
              border: 'none',
              color: '#0284c7',
              fontSize: '0.82rem',
              fontWeight: '800',
              cursor: 'pointer',
              marginBottom: '12px'
            }}
            title="Expand Clinical Snapshot"
          >
            &larr;|
          </button>
          <div
            style={{
              writingMode: 'vertical-rl',
              transform: 'rotate(180deg)',
              fontSize: '0.68rem',
              fontWeight: '800',
              color: '#64748b',
              letterSpacing: '0.08em'
            }}
          >
            CLINICAL SNAPSHOT
          </div>
        </div>
      )}
    </aside>
  );
}
